
import React, { useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import config from "../../config.json";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import TitleBar from "../navbar/TitleBar";
import NavMenu from "../navbar/NavMenu";
import RejectModal from "./RejectModal";

export default function AdminOrgAssign() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [organizationDetails, setOrganizationDetails] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [isRequestView, setIsRequestView] = useState(false);
  const [allFeatures, setAllFeatures] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState({});
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [orgNameToReject, setOrgNameToReject] = useState(location.state?.selectedOrganization?.name || '');

  const defaultCategories = ["vehicle", "stop", "route"];
  const predefinedCategories = [
    "vehicle",
    "stop",
    "route",
    "schedule",
    "site",
    "driver",
    "tracking247",
    "alerts",
    "admin",
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const isRequestPath = currentPath.includes('/request/');
    const isRequestFeaturePath = currentPath.includes('/request/feature');
    setIsRequestView(isRequestPath);

    if (location.state && location.state.selectedOrganization) {
      const orgData = location.state.selectedOrganization;
      setOrganizationDetails(orgData);
      if (orgData.approvalStatus) {
        setApprovalStatus(orgData.approvalStatus);
      }
      // Use requestedFeatures for /request/feature path
      if (isRequestFeaturePath && orgData.requestedFeatures && orgData.requestedFeatures.length > 0) {
        const initialSelectedFeatures = {};
        orgData.requestedFeatures.forEach((reqFeature) => {
          initialSelectedFeatures[reqFeature.featureId] = true;
        });
        setSelectedFeatures(initialSelectedFeatures);
      }
    } else if (id) {
      fetchOrganizationDetails(id);
    }

    getAllTheAvailableFeatures();
  }, [id, location]);

  const fetchOrganizationDetails = async (orgId) => {
    try {
      const response = await axios.get(`${config.baseURL}/organization/${orgId}`, {
        withCredentials: true
      });
      const data = response.data.organization || response.data;
      setOrganizationDetails(data);
      if (data.approvalStatus) {
        setApprovalStatus(data.approvalStatus);
      }
    } catch (error) {
      console.error("Error fetching organization details:", error);
      setError("Failed to load organization details");
    }
  };

  useEffect(() => {
    if (allFeatures.length > 0) {
      const apiCategories = Array.from(
        new Set(allFeatures.map((feature) => feature.api_path.split("/")[2]))
      ).filter((category) => predefinedCategories.includes(category));

      const finalCategories = [...predefinedCategories];
      setCategories(finalCategories);
      setLoading(false);

      if (
        id &&
        !(
          location.pathname.includes('/request/feature') &&
          location.state?.selectedOrganization?.requestedFeatures?.length > 0
        )
      ) {
        fetchOrganizationFeatures(id);
      }
    }
  }, [allFeatures, id, location]);

  const getAllTheAvailableFeatures = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.baseURL}/feature/`, {
        withCredentials: true,
      });
      setAllFeatures(response.data);
    } catch (error) {
      console.error("Error fetching features:", error);
      setError("Failed to load features");
      setLoading(false);
    }
  };

  const fetchOrganizationFeatures = async (organizationId) => {
    try {
      const response = await axios.get(
        `${config.baseURL}/organization/features/${organizationId}`,
        { withCredentials: true }
      );
      const existingFeatures = response.data.features || [];
      const initialSelectedFeatures = {};

      existingFeatures.forEach((feature) => {
        initialSelectedFeatures[feature.feature.id] = true;
      });

      setSelectedFeatures(initialSelectedFeatures);
    } catch (error) {
      console.error("Error fetching organization features:", error);
      setError("Failed to fetch organization features");
    }
  };

  const isAddOnFeature = (feature) => {
    return feature.isDefault === false;
  };

  const getFeaturesByCategory = (category) => {
    return allFeatures.filter((feature) => {
      const pathParts = feature.api_path.split("/");
      const basePath = pathParts[2];

      if (category === "driver" && feature.api_path.includes("/driver")) {
        return true;
      }
      if (category === "site" && feature.api_path.includes("/site")) {
        return true;
      }
      if (category === "admin") {
        return basePath === "user" || basePath === "role";
      }
      if (category === "alerts") {
        return basePath === "alert" || pathParts[3] === "alert";
      }
      return basePath === category;
    });
  };

  const getCategoryCheckboxState = (category) => {
    const regularFeatures = getFeaturesByCategory(category).filter(
      (feature) => !isAddOnFeature(feature)
    );
    const selectedRegularFeatureIds = regularFeatures
      .map((feature) => feature.id)
      .filter((id) => selectedFeatures[id]);
    const allSelected =
      regularFeatures.length > 0 &&
      selectedRegularFeatureIds.length === regularFeatures.length;
    const someSelected = selectedRegularFeatureIds.length > 0 && !allSelected;

    return { checked: allSelected, indeterminate: someSelected };
  };

  const handleCategoryChange = (category) => {
    if (defaultCategories.includes(category)) return;

    const regularFeatures = getFeaturesByCategory(category).filter(
      (feature) => !isAddOnFeature(feature)
    );
    const allSelected = regularFeatures.every(
      (feature) => selectedFeatures[feature.id]
    );

    setSelectedFeatures((prev) => {
      const updatedFeatures = { ...prev };
      regularFeatures.forEach((feature) => {
        updatedFeatures[feature.id] = !allSelected;
      });
      return updatedFeatures;
    });
  };

  const handleAddOnChange = (featureId) => {
    setSelectedFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  };

  const handleClearAll = () => {
    setSelectedFeatures({});
  };

  const getSelectedFeatureIds = () => {
    return Object.keys(selectedFeatures)
      .filter((id) => selectedFeatures[id])
      .map((id) => parseInt(id));
  };

  const handleSave = async () => {
    const selectedIds = getSelectedFeatureIds();
    if (!id) {
      setNotification({
        open: true,
        message: 'No organization ID provided',
        severity: 'error',
      });
      return;
    }
    try {
      const response = await axios.put(
        `${config.baseURL}/organization/features`,
        {
          organizationId: parseInt(id),
          featureIds: selectedIds,
        },
        { withCredentials: true }
      );
      setNotification({
        open: true,
        message: 'Features assigned successfully',
        severity: 'success',
      });
      // navigate(isRequestView ? "/admin" : "/org");
      if (!isRequestView) {
        navigate("/org");
      }else{
        console.log("saved")
      }
    } catch (error) {
      console.error("Error assigning features:", error);
      setNotification({
        open: true,
        message: error.response?.data?.msg || "Failed to assign features",
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    navigate(isRequestView ? "/admin" : "/org");
  };

  const handleViewMore = () => {
    const organizationData = {
      ...organizationDetails,
      approvalStatus
    };

    const orgId = organizationDetails?.organizationId || organizationDetails?.id || id;
    const viewMorePath = isRequestView
      ? `/request/viewMore/${orgId}`
      : `/organization/viewMore/${orgId}`;

    navigate(viewMorePath, {
      state: {
        selectedOrganization: organizationData
      }
    });
  };

  const handleAccept = async () => {
    setIsActionLoading(true);
    try {
      const orgId = location.state?.selectedOrganization?.id
      await axios.post(
        `${config.baseURL}/register/${orgId}/accept`,
        {},
        { withCredentials: true }
      );
      await handleSave();
      setNotification({
        open: true,
        message: 'Request accepted and features assigned successfully',
        severity: 'success',
      });
      navigate("/admin");
    } catch (error) {
      console.error("Error accepting request:", error);
      setNotification({
        open: true,
        message: error.response?.data?.msg || "Failed to accept request",
        severity: 'error',
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleReject = () => {
    setRejectModalOpen(true);
  };

  const confirmReject = async () => {
    setIsActionLoading(true);
    try {
      const orgId = location.state?.selectedOrganization?.id
      await axios.post(
        `${config.baseURL}/register/${orgId}/reject`,
        { reason: rejectionReason },
        { withCredentials: true }
      );
      setRejectModalOpen(false);
      setNotification({
        open: true,
        message: 'Organization rejected successfully',
        severity: 'success',
      });
      navigate('/admin');
    } catch (error) {
      console.error('Error rejecting organization:', error);
      setNotification({
        open: true,
        message: error.response?.data?.msg || 'Failed to reject organization',
        severity: 'error',
      });
      setRejectModalOpen(false);
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const getCategoryDisplayName = (category) => {
    if (category === "tracking247") return "24/7 Tracking";
    if (category === "alerts") return "Alerts & Notifications";
    if (category === "admin") return "Admin Panel";
    return category.charAt(0).toUpperCase() + category.slice(1) + " Management";
  };

  const renderActionButtons = () => {
    if (isRequestView) {
      if (approvalStatus === "REJECTED") {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F2F2F3',
                border: '1px solid #414958',
                color: "#1F242E",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F9E719',
                border: "1px solid #414958",
                color: "#111214",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
                ":hover": { background: '#F9E719' },
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={isActionLoading}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F9E719',
                border: "1px solid #414958",
                color: "#111214",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
                ":hover": { background: '#F9E719' },
              }}
            >
              {isActionLoading ? <CircularProgress size={24

} /> : 'Accept'}
            </Button>
          </Box>
        );
      } else {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleReject}
              disabled={isActionLoading}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#EE3B2B',
                border: "1px solid #414958",
                color: "#FFFFFF",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
                ":hover": { background: '#EE3B2B' },
              }}
            >
              {isActionLoading ? <CircularProgress size={24} color="inherit" /> : 'Reject'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F2F2F3',
                border: '1px solid #414958',
                color: "#1F242E",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              onClick={handleSave}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F9E719',
                border: "1px solid #414958",
                color: "#111214",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
                ":hover": { background: '#F9E719' },
              }}
            >
              Save
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={isActionLoading}
              sx={{
                minWidth: 100,
                height: 55,
                borderRadius: '8px',
                background: '#F9E719',
                border: "1px solid #414958",
                color: "#111214",
                fontFamily: "Roboto",
                fontWeight: "500",
                fontSize: "20px",
                textTransform: "none",
                ":hover": { background: '#F9E719' },
              }}
            >
              {isActionLoading ? <CircularProgress size={24} /> : 'Accept'}
            </Button>
          </Box>
        );
      }
    } else {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
          <Button
            variant="outlined"
            sx={{
              width: "240px",
              height: "55px",
              borderRadius: "8px",
              background: "#FFFFFF",
              border: "1px solid #414958",
              color: "#111214",
              textTransform: "none",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
            }}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            sx={{
              width: "240px",
              height: "55px",
              borderRadius: "8px",
              background: "#FFFFFF",
              border: "1px solid #414958",
              color: "#111214",
              textTransform: "none",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
            }}
            onClick={handleClearAll}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            sx={{
              width: "240px",
              height: "55px",
              borderRadius: "8px",
              background: "#F9E719",
              border: "1px solid #414958",
              color: "#111214",
              "&:hover": { background: "#F9E719" },
              textTransform: "none",
              fontFamily: "Roboto",
              fontWeight: "500",
              fontSize: "20px",
            }}
            onClick={handleSave}
          >
            Save
          </Button>
        </Box>
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title={`Assign Features${organizationDetails?.name ? ` to ${organizationDetails.name}` : ""}`}
          path={isRequestView ? "Organization > New Request > Assign Features" : "Organization > Update Organization > Assign Features"}
          track="View More"
          onTrackClick={handleViewMore}
        />
        <Box
          sx={{
            p: 4,
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading features...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: "#F2F2F3", padding: "20px 0" }}>
        <TitleBar />
        <NavMenu />
        <SubTitle
          title={`Assign Features${organizationDetails?.name ? ` to ${organizationDetails.name}` : ""}`}
          path={isRequestView ? "Organization > New Request > Assign Features" : "Organization > Update Organization > Assign Features"}
          track="View More"
          trackPath={`/organization/viewMore/${id}`}
          onTrackClick={handleViewMore}
        />
        <Box sx={{ p: 4, textAlign: "center", color: "error.main" }}>
          <Typography>{error}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F2F2F3", padding: "0px 0px 20px 0px" }}>
      <TitleBar />
      <NavMenu />
      {approvalStatus === "REJECTED" && (
        <Box
          sx={{
            backgroundColor: '#EE3B2B',
            color: '#721C24',
            height: "36px",
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
            fontFamily: "Roboto",
            fontWeight: 600,
            fontSize: "24px",
            color: "#FFFFFF",
          }}
        >
          Application Rejected
        </Box>
      )}
      <SubTitle
        title={`Assign Features${organizationDetails?.name ? ` to ${organizationDetails.name}` : ""}`}
        path={isRequestView ? "Organization > New Request > Assign Features" : "Organization > Update Organization > Assign Features"}
        track="View More"
        trackPath={`/organization/viewMore/${id}`}
        onTrackClick={handleViewMore}
      />
      <Paper
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
          margin: "0 40px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 2px 5px 0px #00000040",
        }}
      >
        <Box sx={{ padding: "20px" }}>
          <Grid container spacing={3}>
            {categories.map((category) => {
              const categoryFeatures = getFeaturesByCategory(category);
              const regularFeatures = categoryFeatures.filter(
                (feature) => !isAddOnFeature(feature)
              );
              const addOnFeatures = categoryFeatures.filter((feature) =>
                isAddOnFeature(feature)
              );

              if (
                categoryFeatures.length === 0 &&
                !["site", "driver", "tracking247"].includes(category)
              ) {
                return null;
              }

              const { checked, indeterminate } =
                getCategoryCheckboxState(category);

              const isDefaultCategory = defaultCategories.includes(category);

              return (
                <Grid item xs={12} sm={6} md={4} key={category}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: "#FFFFFF",
                      minHeight: "150px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                        padding: isDefaultCategory ? "10px 20px 10px " : "4px",
                        backgroundColor: "#B7BFCC",
                        borderRadius: "8px",
                      }}
                    >
                      {!isDefaultCategory && (
                        <Checkbox
                          checked={checked}
                          indeterminate={indeterminate}
                          onChange={() => handleCategoryChange(category)}
                          disabled={regularFeatures.length === 0}
                          sx={{
                            "&.Mui-checked": { color: "#20A8E0" },
                          }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        sx={{ color: "#111214", fontWeight: 700 }}
                      >
                        {getCategoryDisplayName(category)}
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 2 }}>
                      {regularFeatures.map((feature) => {
                        if (isDefaultCategory && !selectedFeatures[feature.id]) {
                          selectedFeatures[feature.id] = true;
                        }

                        return (
                          <Typography
                            waykey={feature.id}
                            variant="body2"
                            sx={{
                              mb: 1,
                              color: selectedFeatures[feature.id]
                                ? "#111214"
                                : "#666",
                              fontWeight: selectedFeatures[feature.id] ? 500 : 400,
                            }}
                          >
                            {feature.name}
                          </Typography>
                        );
                      })}
                      {categoryFeatures.length === 0 && (
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: "italic", color: "#666" }}
                        >
                          No features available
                        </Typography>
                      )}
                      {addOnFeatures.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              backgroundColor: "#E8EAEE",
                              p: 1,
                              borderRadius: "4px",
                              boxShadow: "-2px 4px 5px 0px #00000040",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 700, color: "#111214" }}
                            >
                              Add-Ons
                            </Typography>
                          </Box>
                          {addOnFeatures.map((addOn) => (
                            <Box
                              key={addOn.id}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              <Checkbox
                                checked={selectedFeatures[addOn.id] || false}
                                onChange={() => handleAddOnChange(addOn.id)}
                                sx={{ "&.Mui-checked": { color: "#20A8E0" } }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: selectedFeatures[addOn.id]
                                    ? "#111214"
                                    : "#666",
                                  fontWeight: selectedFeatures[addOn.id]
                                    ? 500
                                    : 400,
                                }}
                              >
                                {addOn.name}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
            <Grid item xs={12} sx={{ mt: 3 }}>
              {renderActionButtons()}
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={confirmReject}
        isActionLoading={isActionLoading}
        organizationName={orgNameToReject}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
